/**
 * Testigo final en PDF del reporte: carga, reemplazo, borrado y si sustituye al visor.
 *
 * El archivo sube directo a Cloud Storage desde el navegador y después el
 * backend lo asocia al reporte; sin ese segundo paso el PDF no se muestra.
 */
import React, { useRef, useState } from 'react';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebaseConfig';
import {
    TESTIGO_PDF_MAX_BYTES,
    canManageReportFiles,
    formatFileSize,
    testigoPdfPrefix,
} from '../../reportFileUtils';
import { ink, studio } from './studioUi';
import { useIsNarrow } from '../../useMediaQuery';

/** Timestamp de Firestore o cadena ISO → texto corto en español. */
const formatUploadedAt = (value) => {
    if (!value) return '';
    const date = value.toDate && typeof value.toDate === 'function'
        ? value.toDate()
        : value._seconds !== undefined
            ? new Date(value._seconds * 1000)
            : value.seconds !== undefined
                ? new Date(value.seconds * 1000)
                : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
};

const ReportTestigoPdf = ({
    report,
    currentUser,
    userRole,
    userPermissions,
    fetchWithAuth,
    API_URL_INTERNAL,
    onUpdateReportProperties,
}) => {
    const narrow = useIsNarrow();
    const canManage = canManageReportFiles(userRole, userPermissions);
    const testigo = report?.testigoPdf || null;
    const asViewer = !!report?.testigoPdfAsViewer;

    const fileRef = useRef(null);
    const [pendingFile, setPendingFile] = useState(null);
    const [progress, setProgress] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!canManage && !testigo) return null;

    const handlePick = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setError('');
        if (file.type !== 'application/pdf') {
            setError('El testigo debe ser un archivo PDF.');
            return;
        }
        if (file.size > TESTIGO_PDF_MAX_BYTES) {
            setError(`El PDF pesa ${formatFileSize(file.size)} y el máximo es ${formatFileSize(TESTIGO_PDF_MAX_BYTES)}.`);
            return;
        }
        setPendingFile(file);
    };

    const uploadFile = async (file, useAsViewer) => {
        const uid = currentUser?.uid;
        if (!uid) {
            setError('No hay sesión activa para subir el archivo.');
            return;
        }
        setBusy(true);
        setError('');
        setProgress(0);
        try {
            const path = `${testigoPdfPrefix(report.id)}/${uid}_${Date.now()}.pdf`;
            const task = uploadBytesResumable(storageRef(storage, path), file, { contentType: 'application/pdf' });
            await new Promise((resolve, reject) => {
                task.on(
                    'state_changed',
                    (snapshot) => {
                        const pct = snapshot.totalBytes
                            ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
                            : 0;
                        setProgress(pct);
                    },
                    reject,
                    resolve
                );
            });
            const url = await getDownloadURL(task.snapshot.ref);

            const res = await fetchWithAuth(`${API_URL_INTERNAL}/api/reports/${report.id}/testigo`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    url,
                    storagePath: path,
                    filename: file.name,
                    size: file.size,
                    asViewer: !!useAsViewer,
                }),
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(payload.error || 'No se pudo asociar el PDF al reporte.');

            onUpdateReportProperties(report.id, {
                testigoPdf: payload.testigoPdf,
                testigoPdfAsViewer: !!payload.testigoPdfAsViewer,
            });
        } catch (err) {
            setError(err.message || 'No se pudo subir el testigo.');
        } finally {
            setBusy(false);
            setProgress(null);
        }
    };

    const handleToggleViewer = async (next) => {
        setBusy(true);
        setError('');
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/api/reports/${report.id}/testigo/viewer`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ asViewer: next }),
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(payload.error || 'No se pudo cambiar el visor del reporte.');
            onUpdateReportProperties(report.id, { testigoPdfAsViewer: !!payload.testigoPdfAsViewer });
        } catch (err) {
            setError(err.message || 'No se pudo cambiar el visor del reporte.');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('¿Eliminar el testigo PDF de este reporte?')) return;
        setBusy(true);
        setError('');
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/api/reports/${report.id}/testigo`, {
                method: 'DELETE',
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(payload.error || 'No se pudo eliminar el testigo.');
            onUpdateReportProperties(report.id, { testigoPdf: null, testigoPdfAsViewer: false });
        } catch (err) {
            setError(err.message || 'No se pudo eliminar el testigo.');
        } finally {
            setBusy(false);
        }
    };

    const uploadedAt = formatUploadedAt(testigo?.uploadedAt);
    const sizeLabel = formatFileSize(testigo?.size);

    return (
        <div style={narrow ? { ...studio.card, padding: '14px 14px' } : studio.card}>
            <h3 style={{ ...studio.formTitle, margin: '0 0 6px' }}>Testigo final (PDF)</h3>
            <p style={studio.hint}>
                {testigo
                    ? 'Archivo entregable del reporte. Puedes mostrarlo en el visor en lugar del dashboard.'
                    : 'Sube el PDF del testigo final de este reporte.'}
            </p>

            {testigo && (
                <div style={ui.fileRow}>
                    <div style={{ minWidth: 0 }}>
                        <div style={ui.fileName}>{testigo.filename || 'testigo.pdf'}</div>
                        <div style={ui.fileMeta}>
                            {[sizeLabel, uploadedAt].filter(Boolean).join(' · ') || 'PDF cargado'}
                        </div>
                    </div>
                    <a
                        href={testigo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ ...studio.ghostBtn, textDecorationLine: 'none' }}
                    >
                        Abrir en pestaña nueva
                    </a>
                </div>
            )}

            {canManage && (
                <>
                    {testigo && (
                        <label style={ui.viewerToggle}>
                            <input
                                type="checkbox"
                                checked={asViewer}
                                disabled={busy}
                                onChange={(event) => handleToggleViewer(event.target.checked)}
                                style={{ width: 18, height: 18, accentColor: ink.accent }}
                            />
                            <span>Mostrar este PDF en el visor del reporte en lugar del dashboard</span>
                        </label>
                    )}

                    <div style={ui.actions}>
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={busy}
                            style={testigo ? studio.ghostBtn : studio.primaryBtn}
                        >
                            {busy && progress !== null
                                ? `Subiendo… ${progress}%`
                                : testigo ? 'Reemplazar PDF' : 'Agregar archivo PDF'}
                        </button>
                        {testigo && (
                            <button type="button" onClick={handleDelete} disabled={busy} style={studio.dangerBtn}>
                                Eliminar PDF
                            </button>
                        )}
                        <input
                            ref={fileRef}
                            type="file"
                            accept="application/pdf"
                            onChange={handlePick}
                            style={{ display: 'none' }}
                        />
                    </div>

                    {progress !== null && (
                        <div style={ui.progressTrack}>
                            <div style={{ ...ui.progressFill, width: `${progress}%` }} />
                        </div>
                    )}
                </>
            )}

            {error ? <div style={ui.error}>{error}</div> : null}

            {pendingFile && (
                <div style={narrow ? { ...ui.overlay, ...ui.overlayNarrow } : ui.overlay} role="presentation" onClick={() => !busy && setPendingFile(null)}>
                    <div
                        style={narrow ? { ...ui.sheet, ...ui.sheetNarrow } : ui.sheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Visor del reporte"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <p style={studio.kicker}>Testigo final</p>
                        <h3 style={{ ...studio.formTitle, margin: '6px 0 0' }}>
                            ¿El visor del reporte debe mostrar este PDF?
                        </h3>
                        <p style={{ ...studio.hint, marginTop: 10, lineHeight: 1.5, color: ink.muted }}>
                            «{pendingFile.name}» ({formatFileSize(pendingFile.size)}). Si eliges mostrarlo, la vista
                            previa y la vista del usuario final pintarán el PDF en lugar del dashboard. Puedes
                            cambiarlo después con la casilla de esta tarjeta.
                        </p>
                        <div style={ui.sheetActions}>
                            <button
                                type="button"
                                onClick={() => setPendingFile(null)}
                                disabled={busy}
                                style={studio.ghostBtn}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const file = pendingFile;
                                    setPendingFile(null);
                                    uploadFile(file, false);
                                }}
                                disabled={busy}
                                style={studio.ghostBtn}
                            >
                                No, conservar el dashboard
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const file = pendingFile;
                                    setPendingFile(null);
                                    uploadFile(file, true);
                                }}
                                disabled={busy}
                                style={studio.primaryBtn}
                            >
                                Sí, mostrar el PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ui = {
    fileRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginTop: 14,
        padding: 12,
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        backgroundColor: ink.wash,
    },
    fileName: {
        fontWeight: 600,
        color: ink.text,
        fontSize: '0.92rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fileMeta: { color: ink.muted, fontSize: '0.78rem', marginTop: 2 },
    viewerToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 14,
        color: ink.text,
        fontSize: '0.88rem',
        cursor: 'pointer',
    },
    actions: { display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 },
    progressTrack: {
        marginTop: 12,
        height: 6,
        borderRadius: 999,
        backgroundColor: ink.line,
        overflow: 'hidden',
    },
    progressFill: { height: '100%', backgroundColor: ink.accent, transitionProperty: 'width', transitionDuration: '160ms' },
    error: { marginTop: 12, fontSize: '0.8rem', color: ink.danger, fontWeight: 600 },
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.45)',
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
    },
    sheet: {
        ...studio.card,
        width: 'min(520px, 100%)',
        boxShadow: '0 24px 64px rgba(28, 29, 33, 0.22)',
    },
    /* En móvil el diálogo se vuelve hoja inferior. */
    overlayNarrow: { alignItems: 'flex-end', padding: 0, zIndex: 970 },
    sheetNarrow: {
        width: '100%',
        borderRadius: '16px 16px 0 0',
        padding: '18px 16px calc(18px + env(safe-area-inset-bottom))',
    },
    sheetActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, flexWrap: 'wrap' },
};

export default ReportTestigoPdf;
